import { Link, useLocation } from "react-router-dom";
import { Home, Compass, Radio, Zap, Menu, X, Bell, LogOut, ShieldCheck, MonitorPlay, User, Mic } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../hooks/useNotifications";

const SP_LOGO = "https://customer-assets.emergentagent.com/job_emt-task-check/artifacts/gaylzcdf_SP%20Logo%20Main.jpg";
const NAV = [
  { label: "Feed", icon: Home, href: "/" },
  { label: "Explore", icon: Compass, href: "/explore" },
  { label: "Radio", icon: Radio, href: "/radio" },
  { label: "Live", icon: Zap, href: "/live" },
];

export default function MobileNav() {
  const { pathname } = useLocation();
  const { user, role, logout, isCreator, isAdmin } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const { unreadCount, markAllRead } = useNotifications(user?.uid);
  const isActive = (href) => href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <>
      {/* Top header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 h-14 border-b border-white/5" style={{ background: "rgba(17,17,17,0.95)", backdropFilter: "blur(12px)" }}>
        <Link to="/" className="flex items-center gap-2">
          <img src={SP_LOGO} alt="" className="w-7 h-7 rounded-lg object-cover" />
          <span className="font-heading font-black text-white text-base tracking-tight">STAGEPASS</span>
        </Link>
        <div className="flex items-center gap-2">
          {/* Notification bell */}
          {user && (
            <button
              data-testid="mobile-notification-bell"
              className="relative p-1 text-zinc-400"
              onClick={() => unreadCount > 0 && markAllRead()}
            >
              <Bell size={18} />
              {unreadCount > 0 ? (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[8px] font-bold min-w-[14px] h-[14px] rounded-full flex items-center justify-center px-0.5">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              ) : (
                <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-zinc-700" />
              )}
            </button>
          )}
          {!user && (
            <Link to="/auth?mode=register" className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: "#00ff77", color: "#000" }}>
              Join
            </Link>
          )}
          <button onClick={() => setMenuOpen(!menuOpen)} className="p-1 text-zinc-400">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      {/* Dropdown menu */}
      {menuOpen && (
        <div className="md:hidden fixed top-14 left-0 right-0 z-40 border-b border-white/10 px-4 py-3 space-y-1" style={{ background: "#111" }}>
          {isCreator && !isAdmin && (
            <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-zinc-300 text-sm rounded-xl hover:bg-white/5">
              <MonitorPlay size={15} /> Studio
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-zinc-300 text-sm rounded-xl hover:bg-white/5">
              <ShieldCheck size={15} /> Admin
            </Link>
          )}
          {/* Apply as creator for regular users */}
          {user && !isCreator && !isAdmin && (
            <Link to="/apply" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-zinc-300 text-sm rounded-xl hover:bg-white/5">
              <Mic size={15} /> Become a Creator
            </Link>
          )}
          {user && (
            <Link to={isCreator ? "/dashboard/profile" : "/apply"} onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-zinc-300 text-sm rounded-xl hover:bg-white/5">
              <User size={15} /> Profile
            </Link>
          )}
          <div className="border-t border-white/5 pt-2 space-y-1">
            <Link to="/privacy" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-zinc-500 text-xs rounded-xl hover:bg-white/5">Privacy Policy</Link>
            <Link to="/terms" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-zinc-500 text-xs rounded-xl hover:bg-white/5">Terms of Service</Link>
            {user && (
              <button onClick={() => { logout(); setMenuOpen(false); }} className="flex items-center gap-2 px-3 py-2 text-zinc-400 text-sm rounded-xl hover:bg-white/5 w-full">
                <LogOut size={14} /> Sign Out
              </button>
            )}
          </div>
        </div>
      )}

      {/* Bottom tab nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around px-2 h-16 border-t border-white/5" style={{ background: "rgba(17,17,17,0.95)", backdropFilter: "blur(12px)" }}>
        {NAV.map(({ label, icon: Icon, href }) => (
          <Link key={href} to={href}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl transition-colors ${isActive(href) ? "text-[#C026D3]" : "text-zinc-500"}`}>
            <Icon size={20} />
            <span className="text-[10px] font-medium">{label}</span>
          </Link>
        ))}
      </nav>
    </>
  );
}
